import { Alert, Chip } from "@heroui/react";
import { EError, type RestAPI } from "@sc07-canvas/lib";
import { Hammer } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "../components/core/Button";
import { Keybind } from "../components/KeybindModal";
import { UserCard } from "../components/Profile/UserCard";
import { SidebarBase } from "../components/SidebarBase";
import { KeybindManager } from "../lib/keybinds";
import { handleError, oapi } from "../lib/utils";
import { useModerator } from "./Moderator";

export const ModSidebar = () => {
  const { state, dispatch } = useModerator();

  return (
    <SidebarBase
      shown={Boolean(state.showMenu)}
      setSidebarShown={(show) =>
        dispatch({ action: show ? "OPEN_MENU" : "CLOSE_MENU" })
      }
      icon={Hammer}
      title="Moderator"
      side="Left"
    >
      <div className="p-2">
        <SelectedArea />
      </div>
    </SidebarBase>
  );
};

type Pixel = RestAPI.components["schemas"]["ModPixelQuery"]["pixels"][number];

interface IPixelSummary {
  total: number;
  users: {
    user: RestAPI.components["schemas"]["SparseUser"];
    pixels: number;
    topPixels: number;
    hasMostTopPixels: boolean;
  }[];
}

const summarizePixels = (pixels: Pixel[]): IPixelSummary => {
  const users: Map<string, RestAPI.components["schemas"]["SparseUser"]> =
    new Map();
  for (const pixel of pixels) {
    users.set(pixel.userId, pixel.user);
  }

  const userPixels = pixels.reduce(
    (p, c) => {
      if (!c.isTop) return p;

      if (!Object.prototype.hasOwnProperty.call(p, c.userId)) {
        p[c.userId] = 0;
      }
      p[c.userId]++;
      return p;
    },
    {} as { [k: string]: number },
  );
  const topPixelCount = Object.values(userPixels).sort((a, b) => b - a)[0];

  return {
    total: pixels.length,
    users: [...users.values()]
      .map((u) => ({
        user: u,
        pixels: pixels.filter((p) => p.userId === u.sub).length,
        topPixels: pixels.filter((p) => p.userId === u.sub && p.isTop).length,
        hasMostTopPixels:
          pixels.filter((p) => p.userId === u.sub && p.isTop).length ===
          topPixelCount,
      }))
      .sort((a, b) => b.pixels - a.pixels),
  };
};

const SelectedArea = () => {
  const { state } = useModerator();
  const [isLoading, setIsLoading] = useState(false);
  const [pixels, setPixels] = useState<Pixel[]>([]);

  const doFetchQuery = useCallback(
    (from: `${number},${number}`, to: `${number},${number}`) => {
      setIsLoading(true);
      oapi
        .GET("/mod/canvas/query", {
          params: {
            query: {
              from,
              to,
            },
          },
        })
        .then(({ data }) => {
          if (data) setPixels(data.pixels);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  if (!state.selection?.point1 || !state.selection?.point2) {
    return (
      <Alert
        title="No area selected"
        description={
          <>
            Use <Keybind kb={KeybindManager.getKeybind("MOD_SELECT")[0]} /> to
            select 2 regions on the canvas
          </>
        }
      />
    );
  }

  const from = state.selection.point1;
  const to = state.selection.point2;
  const area = [to[0] + 1 - from[0], to[1] + 1 - from[1]];
  const summary = summarizePixels(pixels);

  return (
    <>
      <header>
        Canvas Selection{" "}
        <Chip>
          ({from.join(", ")}) -&gt; ({to.join(", ")})
        </Chip>
        <Chip>
          {area[0]} x {area[1]}
        </Chip>
      </header>

      <Button
        size="sm"
        isLoading={isLoading}
        onPress={() =>
          doFetchQuery(`${from[0]},${from[1]}`, `${to[0]},${to[1]}`)
        }
      >
        Inspect Pixels
      </Button>
      <UndoAreaButton from={from} to={to} />

      <div className="flex flex-col">
        {summary.users.map((user) => (
          <div key={user.user.sub}>
            <UserCard user={user.user} />
            <Chip size="sm">total pixels: {user.pixels}</Chip>
            <Chip
              size="sm"
              color={user.hasMostTopPixels ? "success" : "default"}
            >
              top pixels: {user.topPixels}
            </Chip>
          </div>
        ))}
      </div>
    </>
  );
};

const UndoAreaButton = ({
  from,
  to,
}: {
  from: [x: number, y: number];
  to: [x: number, y: number];
}) => {
  const [loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const doUndo = useCallback(() => {
    setLoading(true);
    oapi
      .POST("/mod/canvas/undo", {
        body: {
          start: { x: from[0], y: from[1] },
          end: { x: to[0], y: to[1] },
        },
      })
      .then(({ error, response }) => {
        if (response.ok) {
          toast.success("Undone area " + JSON.stringify([from, to]));
        } else {
          handleError(new EError(error, response.status));
        }
      })
      .finally(() => {
        setLoading(false);
        setIsConfirming(false);
      });
  }, [from, to]);

  return (
    <>
      <Button
        size="sm"
        isDisabled={isConfirming}
        onPress={() => setIsConfirming(true)}
      >
        Undo Area
      </Button>
      {isConfirming && (
        <>
          <Button size="sm" onPress={() => setIsConfirming(false)}>
            Cancel Undo
          </Button>
          <Button size="sm" color="danger" isLoading={loading} onPress={doUndo}>
            Confirm Undo
          </Button>
        </>
      )}
    </>
  );
};
